import { createAppColumnHelper } from 'design-system';

import type { ColumnVisibilityState } from '@tanstack/react-table';
import { filterFn_arrIncludesSome } from '@tanstack/react-table';

import type { CatalogItem } from '@/entities/catalog-item';
import { arrEqualsSome, filterByNewData, filterByStatus } from './lib/filters';
import { sortAsFrequency, sortAsNumber, sortAsPeriodType } from './lib/sorts';

export const ADDITIONAL_ITEM = 'AdditionalItem';
export const NEW_DATA_URL_PARAM = 'newData';
// Hidden, non-data column carrying the free-text search filter. Its filter
// value is a set of matched row ids (see `useSearchColumnFilter`), computed
// from `filters.search` — the column exists so search participates in the
// table's own `filteredRowModel` (and is excluded from sibling facet counts)
// instead of pre-shaping the `data` array before the table sees it.
export const SEARCH_COLUMN_ID = 'search';
export type CountedLabelFunction = (
  key: string,
  joined?: boolean
) => {
  label: string;
  count: number;
}[];

export const FILTER_KEYS = {
  COMMODITY: 'Commodity',
  TRADING_HUB: 'TradingHub',
  DELIVERY_BASIS: 'DeliveryBasis',
  PRICING_BASIS: 'PricingBasis',
  UNITS: 'Units',
  CURRENCY: 'Currency',
  FREQUENCY: 'Frequency',
  INDEX_MODULE: 'IndexModule'
} as const;

export type FilterKey = (typeof FILTER_KEYS)[keyof typeof FILTER_KEYS];

const countedLabelColumn =
  (key: FilterKey, joined = false) =>
  (getCountedLabels: CountedLabelFunction) =>
    getCountedLabels(key, joined);

export const labeledFilterColumns: Record<
  FilterKey,
  (getCountedLabels: CountedLabelFunction) => ReturnType<CountedLabelFunction>
> = {
  [FILTER_KEYS.COMMODITY]: countedLabelColumn(FILTER_KEYS.COMMODITY),
  [FILTER_KEYS.TRADING_HUB]: countedLabelColumn(FILTER_KEYS.TRADING_HUB),
  [FILTER_KEYS.DELIVERY_BASIS]: countedLabelColumn(FILTER_KEYS.DELIVERY_BASIS),
  [FILTER_KEYS.PRICING_BASIS]: countedLabelColumn(FILTER_KEYS.PRICING_BASIS),
  [FILTER_KEYS.UNITS]: countedLabelColumn(FILTER_KEYS.UNITS),
  [FILTER_KEYS.CURRENCY]: countedLabelColumn(FILTER_KEYS.CURRENCY),
  [FILTER_KEYS.FREQUENCY]: countedLabelColumn(FILTER_KEYS.FREQUENCY),
  [FILTER_KEYS.INDEX_MODULE]: countedLabelColumn(FILTER_KEYS.INDEX_MODULE, true)
};
export const oilOnlyModules = [
  'AfricaCrude',
  'AmericasMarine',
  'AsiaBiofuels',
  'AsiaCrude',
  'AsiaFeedstocks',
  'AsiaFuelOil',
  'AsiaGasoil',
  'AsiaGasoline',
  'AsiaJet',
  'AsiaLPG',
  'AsiaMarine',
  'AsiaNaphtha',
  'EuropeanBiofuels',
  'EuropeanCrude',
  'EuropeanFeedstocks',
  'EuropeanFuelOil',
  'EuropeanGasoil',
  'EuropeanGasoline',
  'EuropeanJet',
  'EuropeanLPG',
  'EuropeanMarine',
  'EuropeanNaphtha',
  'GlobalIndexes',
  'GlobalOilSelect',
  'MiddleEastCrude',
  'MiddleEastFuelOil',
  'MiddleEastGasoil',
  'MiddleEastGasoline',
  'MiddleEastJet',
  'MiddleEastNaphtha',
  'NorthAmericaBiofuels',
  'NorthAmericaCrude',
  'NorthAmericaDistillate',
  'NorthAmericaFeedstocks',
  'NorthAmericaFuelOil',
  'NorthAmericaGasoline',
  'NorthAmericaJet',
  'NorthAmericaNaphtha',
  'NorthAmericaNGLs',
  'NorthAmericaStorage',
  'SouthAmericaCrude'
];

export const energyTransitionModules = [
  'EuropeanSAF',
  'AsiaSAF',
  'NorthAmericaSAF',
  'AsiaBiofuels',
  'EuropeanBiofuels',
  'GlobalHydrogen',
  'GlobalMarineCarbon',
  'GlobalVoluntaryCarbon',
  'NorthAmericaBiofuels',
  'AsiaComplianceCarbon'
];

// Widths of the fixed leading columns that sit in front of Title in both
// spotColumns and forwardCurvesColumns. Exported so anything that needs to
// align with the Title column's start (e.g. ViewInOtherCatalogLink) derives its
// offset from these instead of hand-copying the pixel sum.
export const SYMBOL_COLUMN_WIDTH = 35;
export const FACTSHEET_COLUMN_WIDTH = 50;
export const LAUNCH_DATE_COLUMN_WIDTH = 56;

// Bound to `dataTableFeatures` (rowSelection, rowSorting, columnFiltering,
// columnFaceting, columnSizing, columnVisibility, rowExpanding — see
// `design-system`'s `useDataTable`). Note: `dataTableFeatures` does not
// register `globalFilteringFeature`, so column defs carry no
// `enableGlobalFilter` — that flag was already inert (catalog never calls
// `table.setGlobalFilter`). Free-text search instead runs through the hidden
// `searchColumn` (see `SEARCH_COLUMN_ID`), whose `fuzzyIds` filter value is
// derived from `filters.search` by `useSearchColumnFilter`.
const columnHelper = createAppColumnHelper<CatalogItem>();

const symbolColumn = columnHelper.accessor('Symbol', {
  header: '',
  size: SYMBOL_COLUMN_WIDTH,
  filterFn: arrEqualsSome
});
const searchColumn = columnHelper.accessor(() => undefined, {
  id: SEARCH_COLUMN_ID,
  header: '',
  size: 0,
  enableSorting: false,
  filterFn: 'fuzzyIds'
});
const factsheetColumn = columnHelper.accessor('Factsheet', {
  header: 'Info',
  size: FACTSHEET_COLUMN_WIDTH,
  meta: { align: 'center' },
  // Not a data column — disable sorting so the header click can't sort by a
  // non-existent `Factsheet` value.
  enableSorting: false,
  filterFn: arrEqualsSome
});
const statusColumn = columnHelper.accessor('Status', {
  header: '',
  size: 0,
  filterFn: filterByStatus
});
const launchDateColumn = columnHelper.accessor('LaunchDate', {
  header: '',
  size: LAUNCH_DATE_COLUMN_WIDTH,
  enableSorting: false,
  filterFn: filterByNewData
});
const timeRefColumn = columnHelper.accessor('TimeRef', {
  header: 'TimeRef',
  size: 95,
  sortFn: sortAsNumber,
  filterFn: arrEqualsSome
});
const periodTypeColumn = columnHelper.accessor('PeriodType', {
  header: 'PeriodType',
  size: 120,
  sortFn: sortAsPeriodType,
  filterFn: arrEqualsSome
});
const currencyColumn = columnHelper.accessor('Currency', {
  header: 'Currency',
  size: 100,
  filterFn: arrEqualsSome
});
const commodityColumn = columnHelper.accessor('Commodity', {
  header: 'Commodity',
  size: 130,
  filterFn: arrEqualsSome
});
const deliveryBasisColumn = columnHelper.accessor('DeliveryBasis', {
  header: 'Delivery Basis',
  size: 150,
  filterFn: arrEqualsSome
});
const pricingBasisColumn = columnHelper.accessor('PricingBasis', {
  header: 'Pricing Basis',
  size: 150,
  filterFn: arrEqualsSome
});
const indexModuleColumn = columnHelper.accessor('IndexModule', {
  header: 'Index Module',
  size: 200,
  filterFn: filterFn_arrIncludesSome
});
const factsheetVersionColumn = columnHelper.accessor('FactsheetVersion', {
  header: 'Latest Factsheet',
  size: 170,
  filterFn: arrEqualsSome
});
const timeZoneColumn = columnHelper.accessor('TimeZone', {
  header: 'Timezone',
  size: 140,
  filterFn: arrEqualsSome
});
const holidayCalendarColumn = columnHelper.accessor('HolidayCalendar', {
  header: 'Holiday Calendar',
  size: 220,
  filterFn: arrEqualsSome
});
const lastTradeDateCalendarColumn = columnHelper.accessor('LastTradeDateCalendar', {
  header: 'Last Trade Date Calendar',
  size: 220,
  filterFn: arrEqualsSome
});

export const spotColumns = columnHelper.columns([
  searchColumn,
  symbolColumn,
  factsheetColumn,
  statusColumn,
  launchDateColumn,
  columnHelper.accessor('Title', {
    header: 'Title',
    size: 650,
    meta: { align: 'start' },
    filterFn: arrEqualsSome
  }),
  columnHelper.accessor('Code', {
    header: 'Code',
    size: 100,
    filterFn: arrEqualsSome
  }),
  timeRefColumn,
  periodTypeColumn,
  columnHelper.accessor('Period', { header: 'Period', size: 100, filterFn: arrEqualsSome }),
  columnHelper.accessor('Alias', { header: 'Alias', filterFn: arrEqualsSome }),
  columnHelper.accessor(ADDITIONAL_ITEM, {
    header: ADDITIONAL_ITEM,
    size: 40,
    filterFn: arrEqualsSome
  }),
  columnHelper.accessor('LastDate', { header: 'Last Date', size: 100, filterFn: arrEqualsSome }),
  columnHelper.accessor('Last', {
    header: 'Last',
    size: 100,
    meta: { align: 'end' },
    sortFn: sortAsNumber,
    filterFn: arrEqualsSome
  }),
  columnHelper.accessor('Change', {
    header: 'Change',
    size: 100,
    meta: { align: 'end' },
    sortFn: sortAsNumber,
    filterFn: arrEqualsSome
  }),
  currencyColumn,
  columnHelper.accessor('Units', { header: 'Units', size: 80, filterFn: arrEqualsSome }),
  commodityColumn,
  columnHelper.accessor('TradingHub', {
    header: 'Trading Hub',
    size: 120,
    filterFn: arrEqualsSome
  }),
  deliveryBasisColumn,
  pricingBasisColumn,
  columnHelper.accessor('Frequency', {
    header: 'Frequency',
    size: 120,
    sortFn: sortAsFrequency,
    filterFn: arrEqualsSome
  }),
  indexModuleColumn,
  columnHelper.accessor('PeriodAbs', {
    header: 'Period Abs',
    size: 120,
    filterFn: arrEqualsSome
  }),
  columnHelper.accessor('StartDate', { header: 'Start Date', size: 120, filterFn: arrEqualsSome }),
  columnHelper.accessor('EndDate', { header: 'End Date', size: 120, filterFn: arrEqualsSome }),
  factsheetVersionColumn,
  timeZoneColumn,
  holidayCalendarColumn,
  lastTradeDateCalendarColumn,
  columnHelper.accessor('Increment', {
    header: 'Increment',
    size: 110,
    sortFn: sortAsNumber,
    filterFn: arrEqualsSome
  }),
  columnHelper.accessor('Index', {
    header: 'Columns Available',
    size: 170,
    filterFn: arrEqualsSome
  })
]);

export const forwardCurvesColumns = columnHelper.columns([
  searchColumn,
  symbolColumn,
  factsheetColumn,
  statusColumn,
  launchDateColumn,
  columnHelper.accessor('Title', {
    header: 'Title',
    size: 550,
    meta: { align: 'start' },
    filterFn: arrEqualsSome
  }),
  columnHelper.accessor('Code', { header: 'Code', size: 100, filterFn: arrEqualsSome }),
  timeRefColumn,
  periodTypeColumn,
  columnHelper.accessor('Periods', { header: 'NumPeriods', size: 120, filterFn: arrEqualsSome }),
  currencyColumn,
  columnHelper.accessor('Units', { header: 'Units', size: 100, filterFn: arrEqualsSome }),
  commodityColumn,
  columnHelper.accessor('TradingHub', { header: 'Trading Hub', filterFn: arrEqualsSome }),
  deliveryBasisColumn,
  pricingBasisColumn,
  columnHelper.accessor('Frequency', { header: 'Frequency', size: 120, filterFn: arrEqualsSome }),
  indexModuleColumn,
  columnHelper.accessor('StartDate', { header: 'StartDate', size: 120, filterFn: arrEqualsSome }),
  columnHelper.accessor('EndDate', { header: 'EndDate', size: 120, filterFn: arrEqualsSome }),
  factsheetVersionColumn,
  timeZoneColumn,
  holidayCalendarColumn,
  lastTradeDateCalendarColumn,
  columnHelper.accessor('Increment', { header: 'Increment', size: 110, filterFn: arrEqualsSome }),
  columnHelper.accessor('Index', {
    header: 'Columns Available',
    size: 170,
    sortFn: sortAsNumber,
    filterFn: arrEqualsSome
  }),
  columnHelper.accessor('PeriodMin', {
    size: 0,
    filterFn: arrEqualsSome
  }),
  columnHelper.accessor('PeriodMax', {
    size: 0,
    filterFn: arrEqualsSome
  })
]);

export const defaultVisibilityState: ColumnVisibilityState = {
  [SEARCH_COLUMN_ID]: false,
  Status: false,
  TimeRef: false,
  PeriodType: false,
  Period: false,
  StartDate: false,
  EndDate: false,
  FactsheetVersion: false,
  TimeZone: false,
  HolidayCalendar: false,
  LastTradeDateCalendar: false,
  Increment: false,
  Index: false,
  PricingBasis: false,
  DeliveryBasis: false,
  Frequency: false,
  IndexModule: false,
  PeriodMax: false,
  PeriodMin: false,
  PeriodAbs: false
};

export type DisplayColumn = { displayLabel: string; accessorKey: string };

export const additionalColumnsList: DisplayColumn[] = [
  { displayLabel: 'Period Abs', accessorKey: 'PeriodAbs' }
];

export const defaultColumnsList: DisplayColumn[] = [
  { displayLabel: 'Currency', accessorKey: 'Currency' },
  { displayLabel: 'Units', accessorKey: 'Units' },
  { displayLabel: 'Commodity', accessorKey: 'Commodity' },
  { displayLabel: 'Trading Hub', accessorKey: 'TradingHub' }
];

export const moreColumnsList: DisplayColumn[] = [
  { displayLabel: 'Delivery Basis', accessorKey: 'DeliveryBasis' },
  { displayLabel: 'Start Date', accessorKey: 'StartDate' },
  { displayLabel: 'Holiday Calendar', accessorKey: 'HolidayCalendar' },
  { displayLabel: 'Pricing Basis', accessorKey: 'PricingBasis' },
  { displayLabel: 'End Date', accessorKey: 'EndDate' },
  { displayLabel: 'Last Trade Date Calendar', accessorKey: 'LastTradeDateCalendar' },
  { displayLabel: 'Frequency', accessorKey: 'Frequency' },
  { displayLabel: 'Latest Factsheet', accessorKey: 'FactsheetVersion' },
  { displayLabel: 'Increment', accessorKey: 'Increment' },
  { displayLabel: 'Index Module', accessorKey: 'IndexModule' },
  { displayLabel: 'Timezone', accessorKey: 'TimeZone' },
  { displayLabel: 'Columns Available', accessorKey: 'Index' }
];

export const alphabeticallySortedFilters = ['IndexModule'];
